import { Stack, Typography, Alert, AlertTitle, SxProps, Theme } from '@mui/material';
import React, { useContext, useEffect, useState } from 'react';
import Confetti from 'react-confetti';
import { spaces, palette } from '../../../assets/styles/themes/tokens';
import { useTranslation } from 'react-i18next';
import { aiAssistantContext, AIAssistantDisplayMode } from '../../../state/AIAssistantContextProvider';
import {
  ActiveView,
  MAX_UPLOADS_PER_DAY,
} from './constants';
import { zIndex } from '../../../constants';
import config from 'config';
import { useSelector } from 'react-redux';
import {
  getImagesAvailableAgainAt,
  getPromptsAvailableAgainAt,
} from '../../../reducers/aiAssistant.selectors';
import { format, isValid } from 'date-fns';
import { de } from 'date-fns/locale';
import { notifyBugsnagNetwork } from '../../../utils/bugsnagClient';

const getDialogModeStyles = (dialogFooterRef: React.RefObject<HTMLDivElement> | null): SxProps<Theme> => ({
  position: 'absolute',
  bottom: dialogFooterRef?.current?.offsetHeight ?? 0,
  left: spaces.md,
  right: spaces.md,
  marginBottom: `-${spaces.xs}`,
  width: 'auto',
  zIndex: zIndex.alert,
  pointerEvents: 'none',
});

const pageModeStyles: SxProps<Theme> = {
  position: 'absolute',
  top: spaces.md,
  left: spaces.md,
  right: spaces.md,
  width: 'auto',
  zIndex: zIndex.alert,
  pointerEvents: 'none',
};

const textEditorAlertStyles: SxProps<Theme> = {
  zIndex: 1500,
  bottom: '-117px',
  position: 'absolute',
  left: spaces.md,
  right: spaces.md,
  width: 'auto',
  height: 'fit-content',
  pointerEvents: 'auto',
};
const AUTO_DISMISS_DELAY = 5000;

interface AIAssistantAlertsProps {
  showTextEditorAlertsOnly?: boolean;
}

export const AIAssistantAlerts = ({ showTextEditorAlertsOnly = false }: AIAssistantAlertsProps = {}) => {
  const { t } = useTranslation();
  const {
    dialogFooterRef,
    isUserPromptLimitReached,
    isMessagesRateLimitActive,
    isAttachmentsRateLimitActive,
    displayMode,
    activeView,
    attachmentUploadError,
    setAttachmentUploadError,
    attachmentInfoMessage,
    setAttachmentInfoMessage,
    imageUploadError,
    setImageUploadError,
    documentDownloadAlertVisible,
    setDocumentDownloadAlertVisible,
    imageGenerationLimitReachedAlertVisible,
    setImageGenerationLimitReachedAlertVisible,
    isArtifactGenerationOnboardingAlertVisible,
    setIsArtifactGenerationOnboardingAlertVisible,
    promptLimitAlertVisible,
    setPromptLimitAlertVisible,
  } = useContext(aiAssistantContext);
  const availableAgainAt = useSelector(getPromptsAvailableAgainAt);
  const imagesAvailableAgainAt = useSelector(getImagesAvailableAgainAt);
  const [messagesRateLimitAlertVisible, setMessagesRateLimitAlertVisible] = useState(false);
  const [attachmentsRateLimitAlertVisible, setAttachmentsRateLimitAlertVisible] = useState(false);
  const [userPromptCountAlertVisible, setUserPromptCountAlertVisible] = useState(false);
  const [pageLimitAlertVisible, setPageLimitAlertVisible] = useState(false);

  const isPageMode = displayMode === AIAssistantDisplayMode.PAGE;

  useEffect(() => {
    if (isMessagesRateLimitActive) {
      setMessagesRateLimitAlertVisible(true);
      const timer = setTimeout(() => {
        setMessagesRateLimitAlertVisible(false);
      }, AUTO_DISMISS_DELAY);
      return () => {
        clearTimeout(timer);
        setMessagesRateLimitAlertVisible(false);
      };
    } else {
      setMessagesRateLimitAlertVisible(false);
      return undefined;
    }
  }, [isMessagesRateLimitActive]);

  useEffect(() => {
    if (isAttachmentsRateLimitActive) {
      setAttachmentsRateLimitAlertVisible(true);
      const timer = setTimeout(() => {
        setAttachmentsRateLimitAlertVisible(false);
      }, AUTO_DISMISS_DELAY);
      return () => {
        clearTimeout(timer);
        setAttachmentsRateLimitAlertVisible(false);
      };
    } else {
      setAttachmentsRateLimitAlertVisible(false);
      return undefined;
    }
  }, [isAttachmentsRateLimitActive]);

  useEffect(() => {
    if (isUserPromptLimitReached) {
      setUserPromptCountAlertVisible(true);
      const timer = setTimeout(() => {
        setUserPromptCountAlertVisible(false);
      }, AUTO_DISMISS_DELAY);
      return () => {
        clearTimeout(timer);
        setUserPromptCountAlertVisible(false);
      };
    } else {
      setUserPromptCountAlertVisible(false);
      return undefined;
    }
  }, [isUserPromptLimitReached]);

  useEffect(() => {
    if (attachmentUploadError) {
      const timer = setTimeout(() => {
        setAttachmentUploadError(null);
      }, AUTO_DISMISS_DELAY);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [attachmentUploadError, setAttachmentUploadError]);

  useEffect(() => {
    if (imageUploadError) {
      const timer = setTimeout(() => {
        setImageUploadError(null);
      }, AUTO_DISMISS_DELAY);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [imageUploadError, setImageUploadError]);

  useEffect(() => {
    if (documentDownloadAlertVisible) {
      const timer = setTimeout(() => {
        setDocumentDownloadAlertVisible(false);
      }, AUTO_DISMISS_DELAY);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [documentDownloadAlertVisible, setDocumentDownloadAlertVisible]);

  const containerStyles = isPageMode ? pageModeStyles : getDialogModeStyles(dialogFooterRef);
  const isChatScreen = activeView === ActiveView.CHAT;

  const renderImageUploadErrorAlert = React.useCallback((styles: SxProps<Theme>) => {
    if (!imageUploadError) return null;
    return (
      <Alert 
        severity='error'
        onClose={() => setImageUploadError(null)}
        data-testid='image-upload-error-alert'
        sx={styles}
      >
        <Typography variant='body2' sx={{ color: palette.error.main }}>
          {imageUploadError}
        </Typography>
      </Alert>
    );
  }, [imageUploadError, setImageUploadError]);

  const renderDocumentDownloadAlert = React.useCallback((styles: SxProps<Theme>) => {
    return (
      <Alert 
        severity='success'
        onClose={() => setDocumentDownloadAlertVisible(false)}
        data-testid='download-notification-alert'
        sx={styles}
      >
        <Typography variant='body2' sx={{ color: palette.success.main }}>
          {t('The document is being downloaded')}
        </Typography>
      </Alert>
    );
  }, [t, setDocumentDownloadAlertVisible]);

  if (showTextEditorAlertsOnly) {
    const hasTextEditorAlerts = documentDownloadAlertVisible || imageUploadError;
    if (!hasTextEditorAlerts) {
      return null;
    }
    
    return (
      <Stack direction='column' gap={spaces.xxs} sx={textEditorAlertStyles}>
        {imageUploadError && renderImageUploadErrorAlert({ pointerEvents: 'auto', width: '100%' })}
        {documentDownloadAlertVisible && renderDocumentDownloadAlert({ pointerEvents: 'auto', width: '100%' })}
      </Stack>
    );
  }

  const hasAlerts =
    promptLimitAlertVisible ||
    (isChatScreen && userPromptCountAlertVisible) ||
    (isChatScreen && pageLimitAlertVisible) ||
    (isChatScreen && messagesRateLimitAlertVisible) ||
    (isChatScreen && attachmentsRateLimitAlertVisible) ||
    imageGenerationLimitReachedAlertVisible ||
    (isChatScreen && isArtifactGenerationOnboardingAlertVisible) ||
    Boolean(attachmentUploadError) ||
    Boolean(attachmentInfoMessage) ||
    (documentDownloadAlertVisible && !showTextEditorAlertsOnly && isPageMode) ||
    (imageUploadError && !showTextEditorAlertsOnly && isPageMode);

  if (!hasAlerts) {
    return null;
  }

  return (
    <Stack direction='column' gap={spaces.xxs} sx={containerStyles}>
      {promptLimitAlertVisible && (
        <Alert
          severity='warning'
          onClose={() => setPromptLimitAlertVisible(false)}
          data-testid='prompt-limit-alert'
          sx={{ pointerEvents: 'auto' }}
        >
          <Typography variant='body2' sx={{ color: palette.warning.main }}>
            {(() => {
              if (availableAgainAt) {
                const date = new Date(availableAgainAt);
                if (isValid(date)) {
                  return t(
                    'You have reached your AI quota. You can use AI again from {{weekday}}.',
                    { weekday: format(date, 'EEEE', { locale: de }) }
                  );
                }
                notifyBugsnagNetwork('AIAssistantAlerts', 'invalidAvailableAgainAt', { availableAgainAt });
              }
              return t('You have reached your AI quota.');
            })()}
          </Typography>
        </Alert>
      )}
      {isChatScreen && userPromptCountAlertVisible && (
        <Alert
          severity='info'
          onClose={() => setUserPromptCountAlertVisible(false)}
          sx={{ pointerEvents: 'auto' }}
        >
          <Typography variant='body2' sx={{ color: palette.components.alert.info }}>
            {t(
              'You have reached the limit of {{maxMessages}} messages in this chat. Please start a new chat.',
              { maxMessages: config.ai.readerChat.maxUserPromptCount }
            )}
          </Typography>
        </Alert>
      )}
      {isChatScreen && pageLimitAlertVisible && (
        <Alert 
          severity='info'
          onClose={() => setPageLimitAlertVisible(false)}
          sx={{ pointerEvents: 'auto' }}
        >
          <AlertTitle sx={{ color: palette.info.main }}>
            {t('Chapter is very big')}
          </AlertTitle>
          <Typography variant='body2' sx={{ color: palette.info.main }} data-testid='page-limit-alert'>
            {t('Maximum {{maxDocumentPagesCount}} pages are allowed to be processed at once.',
              { maxDocumentPagesCount: config.ai.readerChat.maxDocumentPagesCount })}
          </Typography>
        </Alert>
      )}
      {isChatScreen && messagesRateLimitAlertVisible && (
        <Alert 
          severity='error'
          onClose={() => setMessagesRateLimitAlertVisible(false)}
          sx={{ pointerEvents: 'auto' }}
        >
          <Typography variant='body2' sx={{ color: palette.error.main }}>
            {t('Message rate limit exceeded. Please try again later.')}
          </Typography>
        </Alert>
      )}
      {isChatScreen && attachmentsRateLimitAlertVisible && (
        <Alert
          severity='error'
          onClose={() => setAttachmentsRateLimitAlertVisible(false)}
          sx={{ pointerEvents: 'auto' }}
        >
          <Typography variant='body2' sx={{ color: palette.error.main }}>
            {t(
              'You\'ve reached the upload limit of {{count}} uploads per 24 hours.'
              + ' You can upload files again tomorrow.',
              { count: MAX_UPLOADS_PER_DAY }
            )}
          </Typography>
        </Alert>
      )}
      {imageGenerationLimitReachedAlertVisible && (
        <Alert
          severity='warning'
          onClose={() => setImageGenerationLimitReachedAlertVisible(false)}
          data-testid='image-generation-limit-reached-alert'
          sx={{ pointerEvents: 'auto' }}
        >
          <AlertTitle sx={{ color: palette.warning.main }}>
            {t('You have reached your image quota.')}
          </AlertTitle>
          {(() => {
            if (imagesAvailableAgainAt) {
              const date = new Date(imagesAvailableAgainAt);
              if (isValid(date)) {
                return (
                  <Typography variant='body2' sx={{ color: palette.warning.main }}>
                    {t(
                      'You can generate images again from {{weekday}}.',
                      { weekday: format(date, 'EEEE', { locale: de }) }
                    )}
                  </Typography>
                );
              }
              notifyBugsnagNetwork('AIAssistantAlerts', 'invalidImagesAvailableAgainAt', {
                availableAgainAt: imagesAvailableAgainAt,
              });
            }
            return null;
          })()}
        </Alert>
      )}
      {isChatScreen && isArtifactGenerationOnboardingAlertVisible && (
        <>
          <Confetti
            width={window.innerWidth}
            height={window.innerHeight}
            recycle={false}
            tweenDuration={3000}
            // eslint-disable-next-line react/forbid-component-props
            style={{ position: 'fixed', top: 0, left: 0, zIndex: zIndex.alert + 1, pointerEvents: 'none' }}
          />
          <Alert
            severity='success'
            onClose={() => setIsArtifactGenerationOnboardingAlertVisible(false)}
            data-testid='artifact-generation-onboarding-success-alert'
            sx={{ pointerEvents: 'auto' }}
          >
            <AlertTitle sx={{ color: palette.success.main }}>
              {t('Your material is ready')}
            </AlertTitle>
            <Typography variant='body2' sx={{ color: palette.success.main }}>
              {t('You can find it anytime in \'My Contents\', for downloading, printing, or further customization.')}
            </Typography>
          </Alert>
        </>
      )}
      {attachmentUploadError && (
        <Alert 
          severity='error'
          onClose={() => setAttachmentUploadError(null)}
          data-testid='ai-assistant-error-alert'
          sx={{ pointerEvents: 'auto' }}
        >
          <AlertTitle sx={{ color: palette.error.main }}>
            {t('Upload failed.')}
          </AlertTitle>
          <Typography variant='body2' sx={{ color: palette.error.main }}>
            {attachmentUploadError}
          </Typography>
        </Alert>
      )}
      {attachmentInfoMessage && (
        <Alert 
          severity='info'
          onClose={() => setAttachmentInfoMessage(null)}
          sx={{ pointerEvents: 'auto' }}
        >
          <Typography variant='body2' sx={{ color: palette.primary.main }}>
            {attachmentInfoMessage}
          </Typography>
        </Alert>
      )}
      {imageUploadError && !showTextEditorAlertsOnly && isPageMode &&
       renderImageUploadErrorAlert({ pointerEvents: 'auto' })}
      {documentDownloadAlertVisible && !showTextEditorAlertsOnly && isPageMode &&
       renderDocumentDownloadAlert({ pointerEvents: 'auto' })}
    </Stack>
  );
};
